import { XIcon } from "@heroicons/react/outline";
import { ArrowLeftRightIcon, DownloadIcon, MaximizeIcon } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import { sizeConfig } from "@/components/ui/MediaBlocks/constants";
import { useMediaFullscreen } from "@/components/ui/MediaBlocks/media-fullscreen-context";
import { ImageBlockProps } from "@/components/ui/MediaBlocks/types";
import { downloadMedia } from "@/components/ui/MediaBlocks/utils";
import { cn } from "@/lib/utils";

export const ImageBlock = ({
  media,
  onDelete,
  onReplace,
  readonly = true,
  size = "md",
}: ImageBlockProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { openFullscreen } = useMediaFullscreen();
  const isXs = size === "xs";

  const handleOpenFullscreen = () => {
    openFullscreen({
      title: media.title || "Image",
      downloadUrl: media.url,
      downloadFilename: media.title || "image",
      variant: "dark",
      maxWidth: "full",
      content: (
        <div className="flex flex-1 items-center justify-center p-4">
          <img
            src={media.url}
            alt={media.title || "Uploaded image"}
            className="max-h-[calc(90vh-64px)] w-auto max-w-[90vw] rounded-lg object-contain"
          />
        </div>
      ),
    });
  };

  return (
    <div
      className={cn(
        "group/media overflow-hidden rounded-lg border border-gray-200 bg-white",
        sizeConfig.container[size],
      )}
    >
      {/* Title bar - hidden for xs */}
      {!isXs && (
        <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-3 py-2">
          <span className="truncate text-sm text-gray-600">{media.title || media.url}</span>
          <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover/media:opacity-100">
            {/* Fullscreen button */}
            <Button
              type="button"
              variant="ghost"
              size="tinyIcon"
              onClick={handleOpenFullscreen}
              aria-label="View fullscreen"
            >
              <MaximizeIcon className="h-3.5 w-3.5" />
            </Button>
            {/* Download button */}
            <Button
              type="button"
              variant="ghost"
              size="tinyIcon"
              onClick={() => downloadMedia(media.url, media.title || "image")}
              aria-label="Download image"
            >
              <DownloadIcon className="h-3.5 w-3.5" />
            </Button>
            {!readonly && onReplace && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="tinyIcon"
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="Replace image"
                >
                  <ArrowLeftRightIcon className="h-3.5 w-3.5" />
                </Button>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) {
                      onReplace(file);
                    }
                  }}
                />
              </>
            )}
            {!readonly && onDelete && (
              <Button
                type="button"
                variant="ghost"
                size="tinyIcon"
                onClick={onDelete}
                aria-label="Delete image"
              >
                <XIcon className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      )}
      {/* Image container - relative for xs overlay */}
      <div className="relative">
        <img
          src={media.url}
          alt={media.title || "Uploaded image"}
          className={cn(
            "cursor-pointer transition-opacity hover:opacity-90",
            isXs ? sizeConfig.image[size] : "h-auto w-full object-cover",
            !isXs && sizeConfig.image[size],
          )}
          onClick={handleOpenFullscreen}
        />
        {/* Overlay actions for xs */}
        {isXs && (
          <div className="absolute inset-0 flex items-center justify-center gap-1 bg-black/40 opacity-0 transition-opacity group-hover/media:opacity-100">
            <Button
              type="button"
              variant="ghost"
              size="tinyIcon"
              onClick={handleOpenFullscreen}
              className="rounded-full bg-white/90 text-gray-600 hover:bg-white"
              aria-label="View fullscreen"
            >
              <MaximizeIcon className="h-3 w-3" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="tinyIcon"
              onClick={() => downloadMedia(media.url, media.title || "image")}
              className="rounded-full bg-white/90 text-gray-600 hover:bg-white"
              aria-label="Download image"
            >
              <DownloadIcon className="h-3 w-3" />
            </Button>
            {!readonly && onReplace && (
              <Button
                type="button"
                variant="ghost"
                size="tinyIcon"
                onClick={() => fileInputRef.current?.click()}
                className="rounded-full bg-white/90 text-gray-600 hover:bg-white"
                aria-label="Replace image"
              >
                <ArrowLeftRightIcon className="h-3 w-3" />
              </Button>
            )}
            {!readonly && onDelete && (
              <Button
                type="button"
                variant="ghost"
                size="tinyIcon"
                onClick={onDelete}
                className="rounded-full bg-white/90 text-gray-600 hover:bg-white"
                aria-label="Delete image"
              >
                <XIcon className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}
      </div>
      {/* Hidden file input for replace - shared between title bar and overlay */}
      {isXs && !readonly && onReplace && (
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*"
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) {
              onReplace(file);
            }
          }}
        />
      )}
    </div>
  );
};
